import { Button, Icons } from "@pankod/refine-antd";
import {
  useApiUrl,
  useCustomMutation,
  useNotification,
} from "@pankod/refine-core";
import { useRef, useState } from "react";

import { GenerateLoginUrlModal } from "./generate-login-url-modal";
import {
  GENERATE_LOGIN_URL_LABEL,
  GENERATE_LOGIN_URL_UNAVAILABLE_LABEL,
  GenerateLoginUrlResponse,
  LOGIN_URL_COPIED_MESSAGE,
  LOGIN_URL_COPY_FAILED_MESSAGE,
  LOGIN_URL_OPENED_MESSAGE,
  getLoginUrlErrorMessage,
  isLoginUrlEligible,
} from "../utils/login-url";

type GenerateLoginUrlActionProps = {
  budgetId?: string;
  controlNumber?: string | number | null;
  name?: string;
  healthInsurerName?: string;
  canExecute: boolean;
};

const openLoginUrl = (url: string): boolean => {
  const win = window.open(url, "_blank", "noopener,noreferrer");
  return Boolean(win);
};

export const GenerateLoginUrlAction: React.FC<
  GenerateLoginUrlActionProps
> = ({ budgetId, controlNumber, name, healthInsurerName, canExecute }) => {
  const apiUrl = useApiUrl();
  const notification = useNotification();
  const [visible, setVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contingencyUrl, setContingencyUrl] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const { mutate, isLoading } = useCustomMutation();

  if (!canExecute) {
    return null;
  }

  const eligible = Boolean(budgetId) && isLoginUrlEligible(controlNumber);
  const pending = isLoading || isSubmitting;
  const disabled = !eligible || pending;
  const buttonClassName = eligible
    ? "generate-login-url-action generate-login-url-action--enabled"
    : "generate-login-url-action generate-login-url-action--blocked";

  const handleClick = () => {
    if (!eligible || pending) {
      return;
    }

    setContingencyUrl(null);
    setVisible(true);
  };

  const handleCancel = () => {
    if (pending) {
      return;
    }

    setVisible(false);
    setContingencyUrl(null);
  };

  const handleConfirm = () => {
    if (!budgetId || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    mutate(
      {
        url: `${apiUrl}/v1/budget/${budgetId}/login-url`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (response) => {
          submittingRef.current = false;
          setIsSubmitting(false);

          const { url } = (response?.data ?? {}) as GenerateLoginUrlResponse;

          if (!url) {
            setVisible(false);
            notification.open?.({
              type: "error",
              message: getLoginUrlErrorMessage(),
            });
            return;
          }

          if (openLoginUrl(url)) {
            setVisible(false);
            notification.open?.({
              type: "success",
              message: LOGIN_URL_OPENED_MESSAGE,
            });
            return;
          }

          setContingencyUrl(url);
        },
        onError: (error) => {
          submittingRef.current = false;
          setIsSubmitting(false);
          setVisible(false);
          notification.open?.({
            type: "error",
            message: getLoginUrlErrorMessage(error),
          });
        },
      },
    );
  };

  const handleRetryOpen = () => {
    if (!contingencyUrl) {
      return;
    }

    if (openLoginUrl(contingencyUrl)) {
      setVisible(false);
      setContingencyUrl(null);
      notification.open?.({
        type: "success",
        message: LOGIN_URL_OPENED_MESSAGE,
      });
    }
  };

  const handleCopy = () => {
    if (!contingencyUrl) {
      return;
    }

    navigator.clipboard
      .writeText(contingencyUrl)
      .then(() => {
        notification.open?.({
          type: "success",
          message: LOGIN_URL_COPIED_MESSAGE,
        });
      })
      .catch(() => {
        notification.open?.({
          type: "error",
          message: LOGIN_URL_COPY_FAILED_MESSAGE,
        });
      });
  };

  return (
    <>
      <Button
        aria-label={GENERATE_LOGIN_URL_LABEL}
        className={buttonClassName}
        disabled={disabled}
        icon={!pending ? <Icons.LinkOutlined /> : undefined}
        loading={pending}
        onClick={handleClick}
        size="small"
        title={
          eligible
            ? GENERATE_LOGIN_URL_LABEL
            : GENERATE_LOGIN_URL_UNAVAILABLE_LABEL
        }
      />
      <GenerateLoginUrlModal
        visible={visible}
        loading={pending}
        budgetName={name}
        controlNumber={controlNumber}
        healthInsurerName={healthInsurerName}
        contingencyUrl={contingencyUrl}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onRetryOpen={handleRetryOpen}
        onCopy={handleCopy}
      />
    </>
  );
};
